import { WardApiClient } from '@/common/services/api/ward-backend/client';
import { studentsSearchEndpoints } from '../endpoints/chatEndpoints';
import type { Student } from '../types';

// Ward API returns lowercase firstname/lastname — map to camelCase
interface WardStudent {
  id: number;
  firstname: string;
  lastname: string;
  email?: string;
  photo?: string;
  current_grade?: string;
  current_site?: string;
}

function mapWardStudent(s: WardStudent): Student {
  return {
    firstName: s.firstname,
    lastName: s.lastname,
    mail: s.email || '',
    photo: s.photo || null,
    current_grade: s.current_grade,
    current_site: s.current_site,
  };
}

export interface SearchStudentsRequest {
  search?: string;
  limit?: number;
  offset?: number;
}

export interface SearchStudentsResponse {
  students: Student[];
  totalCount: number;
  hasMore: boolean;
}

export class StudentsSearchService {
  private static get wardClient() {
    return WardApiClient.getInstance();
  }

  static async searchStudents(
    query: string,
    options: SearchStudentsRequest = {}
  ): Promise<Student[]> {
    const { limit = 10, offset = 0 } = options;
    const response = await this.wardClient.get<WardStudent[]>(
      studentsSearchEndpoints.search(query, limit, offset)
    );
    return response.data.map(mapWardStudent);
  }

  static async searchStudentsWithPagination(
    query: string,
    options: SearchStudentsRequest = {}
  ): Promise<SearchStudentsResponse> {
    const { limit = 10, offset = 0 } = options;
    const response = await this.wardClient.get<{
      students: WardStudent[];
      totalCount: number;
      page: number;
      pageSize: number;
    }>(studentsSearchEndpoints.searchWithPagination(query, limit, offset));

    const { students, totalCount } = response.data;
    return {
      students: students.map(mapWardStudent),
      totalCount,
      hasMore: offset + limit < totalCount,
    };
  }

  static async searchStudentByEmail(email: string): Promise<Student | null> {
    try {
      const response = await this.wardClient.get<WardStudent>(
        studentsSearchEndpoints.getByEmail(email)
      );
      return mapWardStudent(response.data);
    } catch {
      return null;
    }
  }

  static async getStudentById(id: number): Promise<Student | null> {
    try {
      const response = await this.wardClient.get<WardStudent>(studentsSearchEndpoints.getById(id));
      return mapWardStudent(response.data);
    } catch {
      return null;
    }
  }
}
